export interface MetaUpgrade {
  id: string;
  name: string;
  description: string;
  icon: string;
  maxLevel: number;
  currentLevel: number;
  baseCost: number;
  effect: (level: number) => number;
}

export interface MetaStats {
  totalRuns: number;
  totalKills: number;
  longestSurvival: number;
  highestLevel: number;
  catTreats: number;
  totalCatTreatsEarned: number;
}

export class MetaProgression {
  private static readonly STORAGE_KEY = 'rogue-cats-meta';
  private stats: MetaStats;
  private upgrades: Map<string, MetaUpgrade>;

  constructor() {
    this.stats = this.loadStats();
    this.upgrades = this.initializeUpgrades();
    this.loadUpgrades();
  }

  private loadStats(): MetaStats {
    const saved = localStorage.getItem(MetaProgression.STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load meta progression:', e);
      }
    }

    return {
      totalRuns: 0,
      totalKills: 0,
      longestSurvival: 0,
      highestLevel: 0,
      catTreats: 0,
      totalCatTreatsEarned: 0
    };
  }

  private initializeUpgrades(): Map<string, MetaUpgrade> {
    const upgrades = new Map<string, MetaUpgrade>();

    upgrades.set('max_health', {
      id: 'max_health',
      name: 'Stronger Constitution',
      description: 'Increase max health',
      icon: '❤️',
      maxLevel: 10,
      currentLevel: 0,
      baseCost: 50,
      effect: (level) => 100 + (level * 20) // +20 HP per level
    });

    upgrades.set('move_speed', {
      id: 'move_speed',
      name: 'Agility Training',
      description: 'Increase movement speed',
      icon: '⚡',
      maxLevel: 5,
      currentLevel: 0,
      baseCost: 75,
      effect: (level) => 200 + (level * 10) // +10 speed per level
    });

    upgrades.set('damage', {
      id: 'damage',
      name: 'Sharper Claws',
      description: 'Increase all weapon damage',
      icon: '⚔️',
      maxLevel: 10,
      currentLevel: 0,
      baseCost: 60,
      effect: (level) => 1 + (level * 0.1) // +10% damage per level
    });

    upgrades.set('xp_gain', {
      id: 'xp_gain',
      name: 'Quick Learner',
      description: 'Gain more XP from kills',
      icon: '⭐',
      maxLevel: 5,
      currentLevel: 0,
      baseCost: 80,
      effect: (level) => 1 + (level * 0.15) // +15% XP per level
    });

    upgrades.set('pickup_range', {
      id: 'pickup_range',
      name: 'Magnetic Paws',
      description: 'Increase pickup magnet range',
      icon: '🧲',
      maxLevel: 5,
      currentLevel: 0,
      baseCost: 40,
      effect: (level) => 100 + (level * 20) // +20 range per level
    });

    upgrades.set('starting_weapon', {
      id: 'starting_weapon',
      name: 'Extra Weapon',
      description: 'Start with additional weapon level',
      icon: '🎯',
      maxLevel: 3,
      currentLevel: 0,
      baseCost: 100,
      effect: (level) => level // Weapons start at this level
    });

    upgrades.set('cooldown_reduction', {
      id: 'cooldown_reduction',
      name: 'Rapid Reflexes',
      description: 'Reduce weapon cooldowns',
      icon: '⏱️',
      maxLevel: 5,
      currentLevel: 0,
      baseCost: 90,
      effect: (level) => 1 - (level * 0.08) // -8% cooldown per level
    });

    upgrades.set('health_regen', {
      id: 'health_regen',
      name: 'Regeneration',
      description: 'Slowly regenerate health',
      icon: '💚',
      maxLevel: 5,
      currentLevel: 0,
      baseCost: 120,
      effect: (level) => level * 0.5 // +0.5 HP/sec per level
    });

    return upgrades;
  }

  private loadUpgrades(): void {
    const saved = localStorage.getItem(MetaProgression.STORAGE_KEY + '-upgrades');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        for (const [id, level] of Object.entries(data)) {
          const upgrade = this.upgrades.get(id);
          if (upgrade) {
            upgrade.currentLevel = level as number;
          }
        }
      } catch (e) {
        console.error('Failed to load upgrades:', e);
      }
    }
  }

  private save(): void {
    localStorage.setItem(MetaProgression.STORAGE_KEY, JSON.stringify(this.stats));

    const upgradeData: { [key: string]: number } = {};
    this.upgrades.forEach((upgrade, id) => {
      upgradeData[id] = upgrade.currentLevel;
    });
    localStorage.setItem(MetaProgression.STORAGE_KEY + '-upgrades', JSON.stringify(upgradeData));
  }

  public recordRun(survivalTime: number, level: number, enemiesKilled: number): number {
    this.stats.totalRuns++;
    this.stats.totalKills += enemiesKilled;
    this.stats.longestSurvival = Math.max(this.stats.longestSurvival, survivalTime);
    this.stats.highestLevel = Math.max(this.stats.highestLevel, level);

    // Calculate Cat Treats earned
    const baseReward = 10;
    const timeBonus = Math.floor(survivalTime / 10); // 1 treat per 10 seconds
    const levelBonus = level * 5; // 5 treats per level
    const killBonus = Math.floor(enemiesKilled / 5); // 1 treat per 5 kills

    const totalEarned = baseReward + timeBonus + levelBonus + killBonus;
    this.stats.catTreats += totalEarned;
    this.stats.totalCatTreatsEarned += totalEarned;

    this.save();
    return totalEarned;
  }

  public purchaseUpgrade(upgradeId: string): boolean {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade) return false;

    if (upgrade.currentLevel >= upgrade.maxLevel) return false;

    const cost = this.getUpgradeCost(upgradeId);
    if (this.stats.catTreats < cost) return false;

    this.stats.catTreats -= cost;
    upgrade.currentLevel++;
    this.save();
    return true;
  }

  public getUpgradeCost(upgradeId: string): number {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade) return 0;

    // Cost increases with each level: baseCost * (1.5 ^ currentLevel)
    return Math.floor(upgrade.baseCost * Math.pow(1.5, upgrade.currentLevel));
  }

  public getUpgrade(upgradeId: string): MetaUpgrade | undefined {
    return this.upgrades.get(upgradeId);
  }

  public getAllUpgrades(): MetaUpgrade[] {
    return Array.from(this.upgrades.values());
  }

  public getStats(): MetaStats {
    return { ...this.stats };
  }

  public getUpgradeValue(upgradeId: string): number {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade) return 0;
    return upgrade.effect(upgrade.currentLevel);
  }

  public resetProgress(): void {
    // Keep stats but reset upgrades and treats
    this.stats.catTreats = 0;
    this.upgrades.forEach(upgrade => {
      upgrade.currentLevel = 0;
    });
    this.save();
  }
}
